import { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, CloudOff, X } from 'lucide-react';
import { useCatalog } from '@/features/sync/CatalogProvider';
import { describeSyncSummary } from '@/services/sync/CatalogSyncService';
import type { SyncOutcome } from '@/services/sync/CatalogSyncService';

/**
 * Resumo da última sincronização (F7-T12).
 *
 * Só aparece quando há algo que o usuário precisa saber. "Já está atualizado" e
 * "estou offline" não geram banner: são o comportamento normal de um app que passa
 * dias sem rede, e avisar sobre eles vira ruído.
 */
const VISIBLE: Record<SyncOutcome, boolean> = {
  updated: true,
  seeded: true,
  'invalid-remote': true,
  failed: true,
  'up-to-date': false,
  offline: false,
};

const TONE: Partial<Record<SyncOutcome, { className: string; Icon: typeof CheckCircle2 }>> = {
  updated: { className: 'bg-sky-500/15 text-sky-200', Icon: CheckCircle2 },
  seeded: { className: 'bg-sky-500/15 text-sky-200', Icon: CheckCircle2 },
  'invalid-remote': { className: 'bg-amber-500/15 text-amber-200', Icon: AlertTriangle },
  failed: { className: 'bg-amber-500/15 text-amber-200', Icon: CloudOff },
};

export function CatalogUpdateBanner(): React.ReactElement | null {
  const { lastSync, dismissSummary } = useCatalog();

  // Mensagens de sucesso somem sozinhas; as de problema esperam o usuário.
  useEffect(() => {
    if (!lastSync) return;
    if (lastSync.outcome !== 'updated' && lastSync.outcome !== 'seeded') return;

    const timer = setTimeout(dismissSummary, 8000);
    return () => clearTimeout(timer);
  }, [lastSync, dismissSummary]);

  if (!lastSync || !VISIBLE[lastSync.outcome]) return null;

  const tone = TONE[lastSync.outcome] ?? TONE.failed!;
  const { Icon } = tone;

  return (
    <div role="status" className={`mb-3 flex items-start gap-2 rounded-xl p-3 ${tone.className}`}>
      <Icon size={18} className="mt-0.5 shrink-0" aria-hidden />

      <p className="flex-1 text-sm">{describeSyncSummary(lastSync)}</p>

      <button
        type="button"
        onClick={dismissSummary}
        aria-label="Dispensar aviso"
        className="-m-1 shrink-0 rounded p-1 opacity-70 active:opacity-100"
      >
        <X size={16} aria-hidden />
      </button>
    </div>
  );
}
